import React, { useEffect, useRef, useState, useMemo } from "react";
import { fixedGetDataConfig } from "../../../../pageTpls/PublishedListing/config/fixedGetDataConfig";
import { __GetAllContentTypeConfig } from "../../../../utils/getters/gettersV2";
import { useGetDataLazy } from "../../../../utils/react-query-hooks/getData";
import { extractAllBlocksFromTpl, blockRegistry } from "@okf/ootils";
import { useGetQueryData } from "../../../../utils/react-query-hooks/general";
import { SidebarLayoutV2 } from "../../../layout/SidebarLayoutV2";
import { useDynamicLayoutHeight } from "../../InsightMatrix/utils/useDynamicLayoutHeight";
import { useAutoGenFilterConfigsFromTpl } from "../../utils/useAutoGenFilterConfigsFromTpl";
import { getSessionStorageDataForListingPage, getSessionStorageKeyForListingPage } from "../../../../pageTpls/ListingPage/newUtils/sessionStorageUtils";
import { ContentTypeSelector } from "./ContentTypeSelector";
import { DataPivotSidebar } from "./DataPivotSidebar";
import { DataPivotMainContent } from "./DataPivotMainContent";
import { formatCamelCaseToSentenceCase } from "oolib";
import { useUpdateFiltersAndSearchInSessionStorage } from "../utils/useUpdateFiltersAndSearchInSessionStorage";
import { getFilterKeyForBlock } from "@okf/ootils";


const convertTplsToOptions = (tpls) => {
  if (!tpls) return [];

  return tpls.map((d) => ({
    display: d.general?.content?.title || d.kp_content_type,
    value: d.kp_content_type,
  }));
};


export const DataFetcherDataPivot = ({
  contentType: contentTypeProp,
  coverConfig,
  usedIn = 'PublishedListing',
  filterConfig: filterConfigProp,
  presetActiveFilters = [],
}) => {
  
  const allTpls = useRef(__GetAllContentTypeConfig());
  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");
  const { parentWrapperRef, gridHeightCSS } = useDynamicLayoutHeight();

  const onlyPublishingTpls = allTpls.current.filter(
    (tpl) => tpl.general?.segment === "publishing"
  );

  const tplOptions = useMemo(
    () => convertTplsToOptions(onlyPublishingTpls),
    [allTpls.current.length]
  );
  
  const [selectedTpl, setSelectedTpl] = useState(contentTypeProp || tplOptions[0]?.value);
  
  useEffect(() => {
    if (contentTypeProp) {
      setSelectedTpl(contentTypeProp);
    }
  }, [contentTypeProp]);
 

  const pivotableBlocks = useMemo(() => {
    if (!selectedTpl) return [];
    const tpl = allTpls.current.find(d => d.kp_content_type === selectedTpl);
    if (!tpl) return [];
    const allBlocks = extractAllBlocksFromTpl({ tpl });
    return allBlocks
      .filter((bl) => bl.comp !== "StaticRichText" && bl.inputOnly !== true)
      .map(bl => ({
        ...bl,
        display: bl.props?.shortLabel ||
          bl.props?.label ||
          formatCamelCaseToSentenceCase(bl.valuePath.replace("main.", "")),
        value: bl.valuePath,
        filterKey: getFilterKeyForBlock({ block: bl, scope: "tags" }),
      }));
  },[selectedTpl])

  const [activeBlock, setActiveBlock] = useState(
    pivotableBlocks.length > 0 ? pivotableBlocks[0] : undefined
  );
  const [subActiveBlock, setSubActiveBlock] = useState();

  useEffect(() => {
    if (pivotableBlocks.length > 0 && (!activeBlock || !pivotableBlocks.find(bl => bl.valuePath === activeBlock.valuePath))) {
      setActiveBlock(pivotableBlocks[0]);
      setSubActiveBlock(undefined);
    }
  }, [pivotableBlocks, activeBlock]);

  const sessionStorageKey = getSessionStorageKeyForListingPage({contentTypes: [selectedTpl]})
  const sessionStorageData = getSessionStorageDataForListingPage(sessionStorageKey);

  const [activeFilters, setActiveFilters] = useState(
    usedIn === 'PublishedListing' ? (sessionStorageData?.activeFilters || []) : []
  )

  useUpdateFiltersAndSearchInSessionStorage({
    sessionStorageKey,
    activeFilters,
    dontUpdateCondition: usedIn !== 'PublishedListing'
  })
  

  const filterConfig = filterConfigProp || useAutoGenFilterConfigsFromTpl({
    selectedTpls: [
      onlyPublishingTpls.find((tpl) => tpl.kp_content_type === selectedTpl),
    ],
    filterScopes: ["doc"],
    enable: true,
    unifiedFilterGroup: true
  });

  const dynamicFindQuery = useMemo(() => {
    // Include docs taken into moderation — their original was previously published
    // but has `kp_published_status` flipped to "underModeration" during mod. Exclude
    // mod clones so the original isn't double-counted with its clone.
    const baseQuery = {
      ...fixedGetDataConfig.findQuery,
      kp_published_status: { $in: ["published", "underModeration"] },
      kp_is_mod_clone: { $ne: true },
    };

    if (activeBlock?.valuePath) {
      // Array-shaped blocks need `.0` existence check — empty arrays still pass
      // `$exists: true` on the field itself. Registered blocks derive from the
      // registry; unregistered array blocks fall back to the hardcoded list.
      const isArrayShaped =
        blockRegistry.isArrayShaped(activeBlock?.comp) ||
        ["ImageInput", "PDFInput", "VideoInput"].includes(activeBlock?.comp);
      const path = isArrayShaped
        ? `${activeBlock.valuePath}.0`
        : activeBlock.valuePath;
      baseQuery[path] = { $exists: true };
    }

    return baseQuery;
  }, [activeBlock?.valuePath]);

  const { data, status, error , fetchNextPage, hasNextPage, isFetchingNextPage } = useGetDataLazy({
    contentTypes: [selectedTpl],
    ...fixedGetDataConfig,
    findQuery: dynamicFindQuery,
    activeFilters: [...presetActiveFilters, ...activeFilters],
    // searchTerm: true, //if we turn this on, we go elastic search first..
    projection: {
      [activeBlock?.valuePath]: 1,
      [subActiveBlock?.valuePath]: 1,
      'main.title': 1,
      'meta.kp_contributed_by': 1,
      'kp_date_published': 1,
      'meta.kp_content_type': 1
    },
    activeSort: { kp_date_published: -1},
    queryOptions: {
      enabled: !!activeBlock === true,
    },
  });

  const showContentTypeSelector = !contentTypeProp && SELF_MANAGED_BASE_CONFIGS?.listingConfig?.dataPivot?.enable;

  return (
    <div ref={parentWrapperRef}>
      <SidebarLayoutV2
        gridHeightCSS={gridHeightCSS}
        lsbWidth={320}
        lsbComp={
          <div>
            <ContentTypeSelector
              selectedTpl={selectedTpl}
              onTplChange={setSelectedTpl}
              tplOptions={tplOptions}
              isVisible={showContentTypeSelector}
            />
            <DataPivotSidebar
              pivotableBlocks={pivotableBlocks}
              activeBlock={activeBlock}
              setActiveBlock={setActiveBlock}
              subActiveBlock={subActiveBlock}
              setSubActiveBlock={setSubActiveBlock}
              filterConfig={filterConfig}
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              contentTypes={[selectedTpl]}
            />
          </div>
        }
      >
        <div style={{ height: "100%" }}>
          <DataPivotMainContent
            data={data}
            dataFetchStatus={status}
            dataFetchError={error}
            activeBlock={activeBlock}
            subActiveBlock={subActiveBlock}
            contentType={selectedTpl}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}

            filterConfig={filterConfig}
            setActiveFilters={setActiveFilters}
            activeFilters={activeFilters}
            />
        </div>
      </SidebarLayoutV2>
    </div>
  );
};
